// The Prepare & Send toolbar (mockup:
// docs/design/esign-prepare-and-send-toolbar-mockup-v1.html, approved
// 2026-10-14): zoom, who a placed field is for, one click per field type,
// the record picker as a dropdown, and the Panel toggle, in one row above the
// page instead of scattered through the right panel.
//
// CONSUMER: internal-ui.
//
// DECISION: purely presentational. The screen owns every piece of placement
// state (what is armed, who it is for, what is bound) and passes it in; this
// file only draws it and reports clicks. Two copies of "what is armed" is how
// a button lights up for a tool that is not armed.

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  CalendarDays,
  Calendar,
  CaseSensitive,
  ChevronDown,
  CircleDot,
  Database,
  Ellipsis,
  ListChecks,
  Lock,
  Paperclip,
  PanelRight,
  PenLine,
  SquareCheck,
  Strikethrough,
  Type,
} from "lucide-react";

import { FIELD_TYPE_LABEL } from "./prepare-helpers";
import type { SignerColor } from "./prepare-helpers";
import {
  TOOLBAR_MAIN_TYPES,
  TOOLBAR_MORE_TYPES,
  initialsOf,
  type ToolId,
} from "./toolbar-helpers";

const TOOL_ICON: Record<ToolId, typeof Type> = {
  signature: PenLine,
  initials: CaseSensitive,
  date_signed: Calendar,
  text: Type,
  checkbox: SquareCheck,
  dropdown: ListChecks,
  radio: CircleDot,
  editable_date: CalendarDays,
  attachment: Paperclip,
  locked_text: Lock,
  locked_checkbox: SquareCheck,
  record: Database,
  strike: Strikethrough,
};

export interface ToolbarSigner {
  key: string;
  name: string;
  color: SignerColor;
  /** Mapping mode: a slot still on a free word ("Signer 2") rather than a
   *  catalog seat. The menu says so, because a generic slot fills by
   *  position on a send, never by who the person is. */
  generic?: boolean | undefined;
}

/** A catalog seat the mapper can add as a new slot without opening the
 *  Panel (Nate, 2026-10-26: no visible way to map the managing broker). */
export interface ToolbarSeatChoice {
  key: string;
  label: string;
}

export interface PrepareToolbarProps {
  /** Null hides the zoom group (no page to zoom yet). */
  zoom: { fit: boolean; percent: number; onFit: () => void; onOut: () => void; onIn: () => void } | null;
  signers: readonly ToolbarSigner[];
  activeSignerKey: string | null;
  onPickSigner: (key: string) => void;
  /** Mapping mode only: seats this form could still add. Undefined hides
   *  the section. */
  addSeats?: readonly ToolbarSeatChoice[] | undefined;
  onAddSeat?: (roleKey: string) => void;
  /** "Fill now (you)": the sender's own turn. Text and Checkbox place boxes
   *  the sender fills and no signer can change (Nate, 2026-10-23: the only
   *  visible choices were the signers, so the locked tools in More went
   *  unfound). */
  fillNow: boolean;
  onPickFillNow: () => void;
  armed: ToolId | null;
  onArm: (tool: ToolId) => void;
  /** False greys every tool that belongs to a signer. A locked box belongs
   *  to nobody, so it never waits on a signer. */
  canPlaceForSigner: boolean;
  /** Label for the From record button; names the armed record field. */
  recordLabel: string | null;
  /** Strike draws on the page being read, so it waits while a form is being
   *  mapped (a strike is per send and is never saved into a map). */
  canStrike: boolean;
  /** Mapping a blank form: a locked text box is FIXED TEXT, typed once and
   *  printed on every future send (an address, wire instructions). */
  mapping?: boolean;
  recordOpen: boolean;
  onToggleRecord: () => void;
  /** Click elsewhere and Esc close the record dropdown, like the menus. */
  onCloseRecord: () => void;
  /** The picker itself, drawn in the dropdown under the button. */
  recordPicker: ReactNode;
  hint: string | null;
  panel: { collapsed: boolean; onToggle: () => void } | null;
}

export function PrepareToolbar(p: PrepareToolbarProps) {
  const [signerMenu, setSignerMenu] = useState(false);
  const [moreMenu, setMoreMenu] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // One shared "click elsewhere closes the menus", like the other menus here.
  useEffect(() => {
    if (!signerMenu && !moreMenu) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setSignerMenu(false);
        setMoreMenu(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSignerMenu(false);
        setMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [signerMenu, moreMenu]);

  // The record dropdown closes the way the other menus do. The picker's own
  // Esc only works while its search box has focus.
  const { recordOpen, onCloseRecord } = p;
  useEffect(() => {
    if (!recordOpen) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onCloseRecord();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRecord();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [recordOpen, onCloseRecord]);

  const active = p.fillNow ? null : (p.signers.find((s) => s.key === p.activeSignerKey) ?? null);
  const tint = (c: SignerColor | undefined) =>
    c ? { borderColor: c.base, background: c.soft, color: c.deep } : undefined;

  function tool(id: ToolId, label: string, needsSigner: boolean) {
    const Icon = TOOL_ICON[id];
    const lockedTwin = p.fillNow && ((id === "text" && p.armed === "locked_text") || (id === "checkbox" && p.armed === "locked_checkbox"));
    const isArmed = p.armed === id || lockedTwin;
    const senderTool = p.fillNow && (id === "text" || id === "checkbox");
    return (
      <button
        key={id}
        type="button"
        className="esign-tool"
        data-armed={isArmed ? "true" : undefined}
        aria-pressed={isArmed ? "true" : "false"}
        style={isArmed ? tint(active?.color) : undefined}
        disabled={needsSigner && !p.canPlaceForSigner && !senderTool}
        onClick={() => {
          setMoreMenu(false);
          p.onArm(id);
        }}
      >
        <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  const moreArmed = p.armed !== null && ((TOOLBAR_MORE_TYPES as readonly string[]).includes(p.armed) || p.armed === "locked_text" || p.armed === "locked_checkbox" || p.armed === "strike");

  return (
    <div className="esign-toolbar" ref={rootRef} role="toolbar" aria-label="Place fields" data-no-autosave>
      {p.zoom ? (
        <div className="esign-prep-zoom" role="group" aria-label="Zoom">
          <button
            type="button"
            aria-pressed={p.zoom.fit ? "true" : "false"}
            onClick={p.zoom.onFit}
            title="Fit the page to the room it has"
          >
            Fit
          </button>
          <button type="button" aria-label="Zoom out" onClick={p.zoom.onOut}>
            −
          </button>
          <span className="esign-prep-zoom-pct" aria-live="polite">
            {p.zoom.percent}%
          </span>
          <button type="button" aria-label="Zoom in" onClick={p.zoom.onIn}>
            +
          </button>
        </div>
      ) : null}

      <div className="esign-toolbar-wrap">
        <button
          type="button"
          className="esign-placing"
          style={tint(active?.color)}
          aria-haspopup="menu"
          aria-expanded={signerMenu ? "true" : "false"}
          onClick={() => setSignerMenu((v) => !v)}
        >
          <span className="esign-placing-av" style={active ? { background: active.color.base } : undefined} aria-hidden="true">
            {p.fillNow ? "You" : active ? initialsOf(active.name) : "?"}
          </span>
          <span className="esign-placing-tx">
            <span className="esign-placing-lab">Placing for</span>
            <b>{p.fillNow ? "Fill now (you)" : active ? active.name : p.signers.length === 0 ? "Pick a signer in the panel" : "Pick a signer"}</b>
          </span>
          <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
        </button>
        {signerMenu ? (
          <div className="esign-toolbar-menu" role="menu">
            {p.signers.map((s) => (
              <button
                key={s.key}
                type="button"
                role="menuitem"
                className="esign-toolbar-item"
                data-on={s.key === p.activeSignerKey ? "true" : undefined}
                onClick={() => {
                  setSignerMenu(false);
                  p.onPickSigner(s.key);
                }}
              >
                <i className="esign-toolbar-dot" style={{ background: s.color.base }} aria-hidden="true" />
                {s.name}
                {s.generic ? <span className="esign-toolbar-item-note"> · not a seat yet</span> : null}
              </button>
            ))}
            {p.addSeats && p.addSeats.length > 0 && p.onAddSeat ? (
              <>
                <div className="esign-toolbar-menu-head" role="presentation">
                  Add a seat to this form
                </div>
                {p.addSeats.map((seat) => (
                  <button
                    key={`add-${seat.key}`}
                    type="button"
                    role="menuitem"
                    className="esign-toolbar-item"
                    onClick={() => {
                      setSignerMenu(false);
                      p.onAddSeat?.(seat.key);
                    }}
                  >
                    + {seat.label}
                  </button>
                ))}
              </>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="esign-toolbar-item"
              data-on={p.fillNow ? "true" : undefined}
              title="You fill it in now. Text and checkboxes print on the page and the signer cannot change them."
              onClick={() => {
                setSignerMenu(false);
                p.onPickFillNow();
              }}
            >
              <Lock size={14} strokeWidth={1.5} aria-hidden="true" /> Fill now (you)
            </button>
          </div>
        ) : null}
      </div>

      <span className="esign-toolbar-vr" aria-hidden="true" />

      <div className="esign-tools" role="group" aria-label="Field tools">
        {TOOLBAR_MAIN_TYPES.map((t) => tool(t, FIELD_TYPE_LABEL[t], true))}
        <div className="esign-toolbar-wrap">
          <button
            type="button"
            className="esign-tool"
            data-armed={moreArmed ? "true" : undefined}
            aria-haspopup="menu"
            aria-expanded={moreMenu ? "true" : "false"}
            style={moreArmed ? tint(active?.color) : undefined}
            onClick={() => setMoreMenu((v) => !v)}
          >
            <Ellipsis size={20} strokeWidth={1.5} aria-hidden="true" />
            <span>More</span>
          </button>
          {moreMenu ? (
            <div className="esign-toolbar-menu" role="menu">
              {/* YOU FILL IT, THEY CANNOT CHANGE IT (Baker, 2026-09-14): the
                  one tool that belongs to the sender, so it never waits on a
                  signer and never takes a signer's colour. */}
              <button
                type="button"
                role="menuitem"
                className="esign-toolbar-item"
                data-on={p.armed === "locked_text" ? "true" : undefined}
                title={
                  p.mapping
                    ? "Type text once and it prints on this form every time, such as an address or wire instructions. Signers cannot change it."
                    : "You type the value. The signer sees it printed on the page and cannot change it."
                }
                onClick={() => {
                  setMoreMenu(false);
                  p.onArm("locked_text");
                }}
              >
                <Lock size={16} strokeWidth={1.5} aria-hidden="true" /> {p.mapping ? "Fixed text (every send)" : "Locked text"}
              </button>
              {/* CHECKED BY YOU (Nate, 2026-10-15): the sender marks a box
                  checked before the package goes out, instead of leaving
                  it for a signer. Same kind of box as Locked text, so it
                  never waits on a signer either. */}
              <button
                type="button"
                role="menuitem"
                className="esign-toolbar-item"
                data-on={p.armed === "locked_checkbox" ? "true" : undefined}
                title="You check the box. It prints an X on the page and the signer cannot change it."
                onClick={() => {
                  setMoreMenu(false);
                  p.onArm("locked_checkbox");
                }}
              >
                <SquareCheck size={16} strokeWidth={1.5} aria-hidden="true" /> Checked by you
              </button>
              {/* STRIKE THROUGH (toolbar program PR 4a, Grant 2026-10-14):
                  a clause that does not apply is struck before the package
                  goes out. A line drawn into the document, so the signer
                  cannot remove it. Per send; a saved map never brings it
                  back. */}
              <button
                type="button"
                role="menuitem"
                className="esign-toolbar-item"
                data-on={p.armed === "strike" ? "true" : undefined}
                disabled={!p.canStrike}
                title={
                  p.canStrike
                    ? "Draw a line through printed text. It is part of the document; the signer cannot remove it."
                    : "Not while a form is being mapped: a strike belongs to one send, not to the form."
                }
                onClick={() => {
                  setMoreMenu(false);
                  p.onArm("strike");
                }}
              >
                <Strikethrough size={16} strokeWidth={1.5} aria-hidden="true" /> Strike through
              </button>
              {TOOLBAR_MORE_TYPES.map((t) => {
                const Icon = TOOL_ICON[t];
                return (
                  <button
                    key={t}
                    type="button"
                    role="menuitem"
                    className="esign-toolbar-item"
                    data-on={p.armed === t ? "true" : undefined}
                    disabled={!p.canPlaceForSigner}
                    onClick={() => {
                      setMoreMenu(false);
                      p.onArm(t);
                    }}
                  >
                    <Icon size={16} strokeWidth={1.5} aria-hidden="true" /> {FIELD_TYPE_LABEL[t]}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <span className="esign-toolbar-vr" aria-hidden="true" />

      <div className="esign-toolbar-wrap">
        <button
          type="button"
          className="esign-recbtn"
          aria-haspopup="true"
          aria-expanded={p.recordOpen ? "true" : "false"}
          data-armed={p.armed === "record" ? "true" : undefined}
          disabled={!p.canPlaceForSigner}
          onClick={p.onToggleRecord}
        >
          <Database size={20} strokeWidth={1.5} aria-hidden="true" />
          <span className="esign-recbtn-tx">
            <b>{p.recordLabel ?? "From record"}</b>
            <small>Insert transaction data</small>
          </span>
          <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
        </button>
        {p.recordOpen ? <div className="esign-recmenu">{p.recordPicker}</div> : null}
      </div>

      <span className="esign-toolbar-grow" />
      {p.hint ? (
        <span className="esign-toolbar-hint" role="status" aria-live="polite">
          {p.hint}
        </span>
      ) : null}
      {p.panel ? (
        <button
          type="button"
          className="esign-panel-toggle"
          aria-pressed={p.panel.collapsed ? "true" : "false"}
          title={p.panel.collapsed ? "Show the panel" : "Hide the panel so the page has the room"}
          onClick={p.panel.onToggle}
        >
          <PanelRight size={18} strokeWidth={1.5} aria-hidden="true" />
          Panel
        </button>
      ) : null}
    </div>
  );
}
